import { EventSourcePolyfill } from 'event-source-polyfill'
import { backendUrl } from '../../api/apiConfig'
import { getAuthHeaders } from '../../api/axiosClient'
import { ParsedCommand } from '../../types/commandTypes'
import { InternetSearchDecision } from '../../types/chatTypes'

export type ChatStreamRequest = {
  message: string
  chatId?: string
  agentId?: string
  fileIds?: string[]
  /** Catalog model name to answer with; omitted means the tenant default. */
  model?: string
  commands?: ParsedCommand[]
  /** Answers a paused internet search instead of asking a new question. */
  resume?: {
    interruptId: string
    decision: InternetSearchDecision
    // Aligned with the pop-up's searches: the edited query, or null when dropped.
    queries?: (string | null)[]
  }
}

export async function getChatStreamerEvent({
  chatId,
  agentId,
  message,
  fileIds,
  model,
  commands,
  resume,
}: ChatStreamRequest): Promise<EventSourcePolyfill> {
  try {
    const authHeaders = await getAuthHeaders()

    const params = new URLSearchParams({
      conversation_id: chatId || '',
      ...(resume
        ? {
            interrupt_id: resume.interruptId,
            decision: resume.decision,
            ...(resume.queries ? { queries: JSON.stringify(resume.queries) } : {}),
          }
        : { message: message }),
    })

    if (fileIds && fileIds.length > 0) {
      fileIds.forEach(fileId => {
        params.append('file_ids', fileId)
      })
    }

    if (model) {
      params.append('model', model)
    }

    // Each command is sent as its own JSON-encoded `commands` query param
    // ({"name": string, "args": string[]}); multiple params union together.
    if (commands && commands.length > 0) {
      commands.forEach(command => {
        params.append('commands', JSON.stringify({ name: command.name, args: command.args }))
      })
    }

    // The agent endpoint: `resume` answers a paused internet search, `stream` starts a turn.
    const url = `${backendUrl}api/chat/agents/${agentId}/${resume ? 'resume' : 'stream'}?${params.toString()}`;

    return new EventSourcePolyfill(url, {
      headers: {
        ...authHeaders,
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache, no-transform',
        'Connection': 'keep-alive',
        'X-Accel-Buffering': 'no',
      },
      heartbeatTimeout: 180000,
      withCredentials: true,
    })
  } catch (error) {
    console.error('Error creating EventSource:', error)
    throw new Error('Failed to connect to the server. Please check your network connection and try again.')
  }
}
